import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';

/** Types of `GET /search` (docs/dev/api-shell.md — Search). */

export type SearchType = 'contact' | 'deal' | 'property' | 'unit' | 'document' | 'ticket';

export interface SearchHit {
  id: string;
  type: SearchType;
  title: string;
  subtitle: string | null;
  href: string | null;
  locked: boolean;
}

export interface SearchGroup {
  type: SearchType;
  label: string;
  total: number;
  hits: SearchHit[];
}

export interface RecentItem {
  type: SearchType;
  id: string;
  title: string;
  href: string;
}

export interface QuickAction {
  key: string;
  label: string;
  href: string;
}

export interface SearchResponse {
  groups: SearchGroup[];
  suggestion: string | null;
  recent: RecentItem[];
  quickActions: QuickAction[];
}

/** Singular labels for the subtitle of a recent or locked entry ("Kontakt · Inhalt verborgen"). */
export const typeLabels: Record<SearchType, string> = {
  contact: 'Kontakt',
  deal: 'Deal',
  property: 'Objekt',
  unit: 'Einheit',
  document: 'Dokument',
  ticket: 'Ticket',
};

export const SEARCH_LIMIT = 8;
export const SEARCH_LIMIT_ALL = 20;

export function useSearch(q: string, limit: number, enabled: boolean): UseQueryResult<SearchResponse, Error> {
  return useQuery({
    queryKey: ['search', q, limit],
    queryFn: ({ signal }) =>
      api<SearchResponse>(`/search?q=${encodeURIComponent(q)}&limit=${limit}`, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Records an opened record for "Zuletzt geöffnet"; a failure never blocks the navigation. */
export function recordOpened(type: SearchType, id: string): Promise<boolean> {
  return api<void>('/search/opened', { method: 'POST', body: { type, id } }).then(
    () => true,
    (error: unknown) => {
      console.warn('search/opened failed', error);
      return false;
    },
  );
}

/** Splits `text` at the first case-insensitive occurrence of `query`: [before, match, after] or null. */
export function splitHighlight(text: string, query: string): [string, string, string] | null {
  const needle = query.trim().toLocaleLowerCase('de');
  if (needle.length === 0) return null;
  const index = text.toLocaleLowerCase('de').indexOf(needle);
  if (index < 0) return null;
  return [text.slice(0, index), text.slice(index, index + needle.length), text.slice(index + needle.length)];
}
